// /kg-graph — Knowledge Graph canvas + admin tab. Mounted under _admin
// so auth + org gates run before the route loads. Tab is encoded in the
// search param so the URL is shareable + persists across navigation.
//
// No requireRole gate: the canvas (read endpoints) is open to any org
// member; admin-only sections (analyze/replay/runs) gate themselves via
// useIsOrgAdmin + BE 403 fallback. Mirrors the media route pattern
// where read is org-wide and write is admin-checked inline.
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import KGGraphPage from "@/features/kg-graph/components/kg-graph-page";
import { requireFeatureEnabled } from "@/features/features";

const kgGraphSearchSchema = z.object({
  tab: z.enum(["canvas", "admin", "quality", "governance"]).optional().catch(undefined),
  // Deep-link from KB↔KG bridge panel: entityId to focus on canvas mount.
  // Canvas reads this via Route.useSearch() and pans + opens the side panel.
  focus: z.string().optional().catch(undefined),
  // Community drawer deep-link emitted by the canvas when a community
  // super-node is selected.
  community: z.string().optional().catch(undefined),
  // Visible canvas scope selector. Default is enterprise; schema is used
  // by KB detail deep-links for deterministic CSV/JSON/SQL/code structure.
  scope: z.enum(["enterprise", "schema", "provenance"]).optional().catch(undefined),
  // Governance sub-tab. Validated at the component layer via
  // `isGovernanceSubtab` so an unknown value falls back to the default
  // sub-tab without 404'ing the route.
  sub: z.string().optional().catch(undefined),
  // Sensitive-access subtab filters. Prefix `sa` namespaces them so future
  // subtabs can add their own filter params without collision. All optional
  // — the filter bar treats any subset as "active filters". `saResource`
  // is a closed enum so a hand-typed URL param cannot break the filter
  // bar's strict ResourceType union.
  saUser: z.string().optional().catch(undefined),
  saResource: z.enum(["knowledge", "kg_entity", "kg_relation"]).optional().catch(undefined),
  saFrom: z.string().optional().catch(undefined),
  saTo: z.string().optional().catch(undefined),
  // Dedup sub-tab status filter. Prefix `dd` namespaces it so future subtabs
  // can add filter params without collision. Closed enum mirrors StatusFilter
  // in dedup-queue-route; "all" means no status filter.
  ddStatus: z.enum(["pending", "dismissed", "expired", "all"]).optional().catch(undefined),
  ddMethod: z.enum(["embedding", "exact_name", "fuzzy", "all"]).optional().catch(undefined),
  ddAuto: z.enum(["true", "false", "all"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_admin/kg-graph")({
  beforeLoad: () => requireFeatureEnabled("kg"),
  validateSearch: kgGraphSearchSchema.parse,
  component: KGGraphPage,
});
